// VideoCard.jsx — the Video Card MODAL was retired on 2026-09-13
// (Context.HeliconflowProductionPage.md P-1): every surface now opens the
// standalone Production page at /production/:id (pages/Production.jsx). This file
// keeps the pieces the page REUSES: the pipeline constants, the Cooking phase
// banner and the Script Characters panel.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

export const PIPELINE_STEPS = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted'];

export const LIVE_TRANSITIONS = {
  Scripted:   ['Filming', 'Incomplete', 'Archived'],
  Filming:    ['Filmed',  'Scripted',  'Incomplete', 'Archived'],
  Filmed:     ['Editing', 'Filming',   'Incomplete', 'Archived'],
  Editing:    ['Qual',    'Filmed',    'Incomplete', 'Archived'],
  Qual:       ['Approved','Editing'],
  Approved:   ['Posted',  'Qual'],
  Posted:     ['Approved'],
  Incomplete: ['Filming', 'Archived'],
  // Archived = set aside for now, may shoot later (nothing is wrong — that's
  // Incomplete). Restorable to the start of the pipeline or straight to set.
  Archived:   ['Filming', 'Scripted'],
};

export const BACK_STEP = {
  Filming:  'Scripted',
  Filmed:   'Filming',
  Editing:  'Filmed',
  Qual:     'Editing',
  Approved: 'Qual',
  Posted:   'Approved',
  Archived: 'Scripted',
};

export const PILL_CLASS = {
  Scripted:   'pill-scripted',
  Filming:    'pill-filming',
  Filmed:     'pill-filmed',
  Editing:    'pill-editing',
  Qual:       'pill-qual',
  Approved:   'pill-approved',
  Posted:     'pill-posted',
  Incomplete: 'pill-incomplete',
  Archived:   'pill-archived',
};

export function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function toInputDate(str) {
  if (!str) return '';
  return typeof str === 'string' ? str.slice(0, 10) : new Date(str).toISOString().slice(0, 10);
}

// ── Cooking workflow banner ───────────────────────────────────────────────────
// Rendered inside VideoCard when record.isCooking. Surfaces which phase this
// card represents and the cross-phase actions: from Phase 1 you can either
// "Write Phase 2 Script" (creates the Phase 2 row + TH script transactionally
// via POST /api/cooking/phase2) or open the existing Phase 2 card. From Phase
// 2 you can jump back to Phase 1 and see its video/frame_link as a reference.
export function CookingPhaseBanner({ record, members, user, onClose, onRecordUpdated }) {
  const navigate = useNavigate();
  const isPhase1 = record.cookingPhase === 1;
  const isPhase2 = record.cookingPhase === 2;
  const sibling  = isPhase1 ? record.cookingPhase2 : record.cookingPhase1;

  const roles      = user?.roles || [];
  const canWritePhase2 = ['Writer', 'Admin', 'Power User', 'Power_User'].some(r => roles.includes(r));
  const writerMembers  = (members || []).filter(m => (m.roles || []).some(r => r === 'Writer' || r.role === 'Writer'));

  const [creating, setCreating] = useState(false);

  const navigateToCard = useCallback((id) => {
    if (onClose) onClose();
    navigate(`/production/${id}`);
  }, [onClose, navigate]);

  const handlePreassignWriter = useCallback(async (writerId) => {
    try {
      const r = await api.patch(`/api/live-production/${record.id}`, {
        phase2PreassignedWriterId: writerId || null,
      });
      onRecordUpdated?.(r.data);
    } catch {
      alert('Failed to update Phase 2 writer.');
    }
  }, [record.id, onRecordUpdated]);

  const handleWritePhase2 = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post('/api/cooking/phase2', { phase1LiveProductionId: record.id });
      const newScriptId = r.data?.script?.id;
      if (onClose) onClose();
      if (newScriptId) {
        navigate('/scripts', { state: { openScriptId: newScriptId, fromVideoCard: true } });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to start Phase 2 script.';
      alert(msg);
    } finally {
      setCreating(false);
    }
  }, [record.id, creating, onClose, navigate]);

  const phaseLabel  = isPhase1 ? 'Phase 1 — Cooking Footage' : 'Phase 2 — Talking Head';
  const siblingLabel = isPhase1 ? 'Phase 2 — Talking Head' : 'Phase 1 — Cooking Footage';

  return (
    <div className="vc-field full" style={{
      padding: '14px 16px',
      borderRadius: 'var(--radius-md)',
      background: 'rgba(255,160,50,0.06)',
      border: '1px solid rgba(255,160,50,0.25)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>🍳</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,160,50,1)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {phaseLabel}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 2 }}>
              Cooking workflow · {record.client?.name}
            </div>
          </div>
        </div>

        {/* Right side: action / sibling link */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {sibling ? (
            <>
              <span style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{siblingLabel}:</span>
              <span className={`pill ${PILL_CLASS[sibling.status] || ''}`}>{sibling.status}</span>
              <button
                className="btn-ghost"
                style={{ fontSize: 11, padding: '4px 10px' }}
                onClick={() => navigateToCard(sibling.id)}
              >
                {isPhase1 ? 'Open Phase 2 ↗' : '← View Phase 1'}
              </button>
            </>
          ) : isPhase1 ? (
            <>
              {writerMembers.length > 0 && (
                <select
                  value={record.phase2PreassignedWriterId || ''}
                  onChange={e => handlePreassignWriter(e.target.value)}
                  className="vc-input"
                  style={{ fontSize: 11, padding: '4px 8px', minWidth: 180 }}
                  title="Pre-assign the Phase 2 writer (used for the notification when Phase 1 is filmed)"
                >
                  <option value="">Phase 2 writer: default (Phase 1 writer)</option>
                  {writerMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              )}
              {canWritePhase2 && (
                <button
                  className="btn-primary"
                  style={{ fontSize: 11, padding: '6px 14px' }}
                  onClick={handleWritePhase2}
                  disabled={creating || record.status !== 'Approved'}
                  title={record.status !== 'Approved'
                    ? `Phase 1 video must be Approved before Phase 2 writing can begin (currently ${record.status}).`
                    : 'Create the Phase 2 TH script tied to this Cooking project'}
                >
                  {creating ? 'Creating…' : 'Write Phase 2 Script'}
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* The Phase-1 Frame.io reference banner was removed in Run 5 (§7.3):
          Frame.io is decommissioned and Phase 1 footage lives in Heliconflow
          Storage — live had 23 Phase-2 cards and zero with a Phase-1 frame
          link, so the banner could only mislead. Per S7 the FIELDS survive:
          frame_link / frame_folder_link stay on the record and stay editable
          in the form above. A Storage replacement link is a real feature and
          was deliberately not built here. */}
    </div>
  );
}

// ── Script Characters panel ──────────────────────────────────────────────────
// Skit/Mockumentary + Approved scripts. Auto-loaded from /api/scripts/:id/
// characters. Gender + cam tags feed the Schedule Plan modal's cam grouping;
// the talent picker is the per-script cast override. Script List membership is
// DERIVED (Run 3, D-050) — there is no "send" step any more, so this panel has
// no button: scripts appear in the Script List's Ready tab on their own.
// Edit-gated to Producer / Power User / Admin.
// DEF-108 (2026-09-17): the UNCAST chip used to be an inert span that overflowed
// its 150px column and sat UNDER the row's ✕ "Remove this character" button, so a
// click on the chip's right half deleted the row (the chip "disappeared"). The chip
// is now a button that opens the talent picker, the talent cell owns enough width
// and clips its overflow, and nothing in the row deletes without the ✕ itself.
// D-111: a row with no per-script override shows the PROJECT DEFAULT (registry)
// the cards resolve with, muted — not UNCAST — so the panel, the plan modal and
// the talent column agree. `refreshKey` re-fetches when the body was saved.
export function ScriptCharactersPanel({ scriptId, clientId, user, refreshKey }) {
  const TAL_ROLES = ['Producer', 'Power User', 'Power_User', 'Admin'];
  const canEdit   = (user?.roles || []).some(r => TAL_ROLES.includes(r));

  const [data,        setData]        = useState(null); // { characters, staleCharacterIds, talStatus }
  const [loading,     setLoading]     = useState(true);
  const [savingId,    setSavingId]    = useState(null);
  const [deletingId,  setDeletingId]  = useState(null);
  const [refreshing,  setRefreshing]  = useState(false);
  const [collapsed,   setCollapsed]   = useState(false);
  const [errMsg,      setErrMsg]      = useState('');
  // Run 2 / Build 3: active Talent-role members for the per-script override
  // picker, and the client registry for §3.1's note-only display (a casting
  // note is NEVER a casting — it renders as an explicit "uncast" + muted note).
  const [talents,     setTalents]     = useState([]);
  const [registry,    setRegistry]    = useState([]);
  const [pickingId,   setPickingId]   = useState(null); // DEF-108: row whose talent picker is open
  useEffect(() => {
    if (!canEdit) return;
    api.get('/api/team-members')
      .then(r => setTalents((r.data || []).filter(m =>
        m.isActive !== false && m.roles?.some(x => x.role === 'Talent'))))
      .catch(() => {});
  }, [canEdit]);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/api/client-characters?clientId=${clientId}`)
      .then(r => setRegistry(r.data.characters || []))
      .catch(() => {});
  }, [clientId]);
  const registryNote = (name) => {
    const key = String(name || '').replace(/^\s+|\s+$/g, '').toLowerCase();
    const row = registry.find(r => !r.isArchived &&
      String(r.name).replace(/^\s+|\s+$/g, '').toLowerCase() === key);
    return row?.castingNote || null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/scripts/${scriptId}/characters`);
      setData(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [scriptId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  async function patchCharacter(characterId, patch) {
    setSavingId(characterId);
    setErrMsg('');
    try {
      const r = await api.patch(`/api/scripts/${scriptId}/characters/${characterId}`, patch);
      setData(prev => ({
        ...prev,
        // PATCH returns the bare row — keep the GET's registry-default fields (D-111).
        characters: prev.characters.map(c => c.id === characterId ? { ...c, ...r.data } : c),
      }));
      if (patch.talentId !== undefined) setPickingId(null);
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'Failed to save.');
    } finally { setSavingId(null); }
  }

  async function deleteCharacter(characterId, name) {
    if (!window.confirm(`Remove character "${name}"? This can't be undone.`)) return;
    setDeletingId(characterId);
    setErrMsg('');
    try {
      await api.delete(`/api/scripts/${scriptId}/characters/${characterId}`);
      setData(prev => ({
        ...prev,
        characters: prev.characters.filter(c => c.id !== characterId),
        staleCharacterIds: (prev.staleCharacterIds || []).filter(id => id !== characterId),
      }));
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'Failed to remove.');
    } finally { setDeletingId(null); }
  }

  async function refreshCharacters() {
    setRefreshing(true);
    setErrMsg('');
    try {
      await api.post(`/api/scripts/${scriptId}/characters/refresh`);
      await load();
    } catch (e) {
      setErrMsg(e.response?.data?.error || 'Refresh failed.');
    } finally { setRefreshing(false); }
  }

  // Hide entirely until loaded so non-Skit/non-Approved scripts don't flash
  // a "Loading characters…" line before the gate kicks in below.
  if (loading || !data) return null;

  // Skit + Mockumentary (the character-column formats, DEF-083 — Run 2 extended
  // this from Skit-only), and only useful once the script is Approved.
  if (!['Skit', 'Mockumentary', 'Longform'].includes(data.format) || data.qualStatus !== 'Approved') return null; // Longform joined 2026-09-15 (D-089)

  const { characters, staleCharacterIds = [], talStatus, scheduled, hasShootDay } = data;
  const staleSet   = new Set(staleCharacterIds);
  // D-095: one click clears every stale row (bad imports can leave dozens).
  const removeAllStale = async () => {
    if (!window.confirm(`Remove ${staleCharacterIds.length} character${staleCharacterIds.length === 1 ? '' : 's'} no longer in the script?`)) return;
    try { await api.delete(`/api/scripts/${scriptId}/characters/stale`); setData(prev => ({ ...prev, characters: prev.characters.filter(c => !staleSet.has(c.id)), staleCharacterIds: [] })); }
    catch (e) { alert(e.response?.data?.error || 'Could not remove the stale characters.'); }
  };
  // Stale rows (no longer in the script body) are ignored by the tag count —
  // they can be deleted via the row's ✕.
  const liveChars  = characters.filter(c => !staleSet.has(c.id));
  const untagged   = liveChars.filter(c => !c.talReady);

  const statusChip = (() => {
    // Membership is derived (D-050). 2026-09-02 overhaul: segments existing
    // means ASSIGNED; only a segment with an actual shoot day is SCHEDULED.
    const map = {
      scheduled: { bg: 'rgba(139,109,255,0.14)', fg: 'var(--violet)',        text: 'Scheduled' },
      assigned:  { bg: 'rgba(230,200,110,0.12)', fg: 'rgba(230,200,110,0.95)', text: 'Assigned' },
      hidden:    { bg: 'rgba(140,140,160,0.12)', fg: 'var(--tx-secondary)', text: 'Hidden from Script List' },
      ready:     { bg: 'rgba(74,222,128,0.12)',  fg: '#4ade80',             text: 'In the Script List' },
    };
    const s = scheduled
      ? (hasShootDay ? map.scheduled : map.assigned)
      : (talStatus === 'not_ready' ? map.hidden : map.ready);
    return (
      <span style={{ padding: '2px 8px', borderRadius: 3, background: s.bg, color: s.fg, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {s.text}
      </span>
    );
  })();

  return (
    <div className="vc-field full" style={{
      padding: '12px 14px',
      borderRadius: 'var(--radius-md)',
      background: 'var(--bg-hover)',
      border: '1px solid var(--border)',
    }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setCollapsed(v => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11 }}>{collapsed ? '▸' : '▾'}</span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--tx-muted)' }}>
            Characters · {characters.length}
          </span>
          {statusChip}
        </div>
        {canEdit && !collapsed && (
          <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
            <button
              className="btn-ghost"
              style={{ fontSize: 10, padding: '3px 8px' }}
              onClick={refreshCharacters}
              disabled={refreshing}
              title="Re-scan the script body for new characters"
            >
              {refreshing ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <div style={{ marginTop: 12 }}>
          {staleCharacterIds.length > 0 && canEdit && (
            <div style={{ marginBottom: 8 }}>
              <button type="button" className="btn-ghost" data-testid="remove-stale-characters" style={{ fontSize: 11, padding: '3px 10px', color: 'var(--s-incomplete-t)' }} onClick={removeAllStale} title="Delete every character row whose name is no longer in the script (bad imports can leave dozens)">✕ Remove {staleCharacterIds.length} stale character{staleCharacterIds.length === 1 ? '' : 's'}</button>
            </div>
          )}
          {errMsg && (
            <div style={{ fontSize: 11, color: 'var(--s-incomplete-t)', marginBottom: 8 }}>
              {errMsg}
            </div>
          )}
          {characters.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--tx-faint)' }}>
              No characters extracted yet. Click ↻ Refresh to scan the script body.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {characters.map(c => {
                const stale = staleSet.has(c.id);
                return (
                  <div
                    key={c.id}
                    style={{
                      display: 'grid',
                      // 2026-09-03 (owner call): the gender dropdown is gone —
                      // the freed 140px goes to the character name column.
                      // DEF-108: the talent cell is wide enough for picker + chip and
                      // clips its own overflow — nothing spills under the ✓ or the ✕.
                      gridTemplateColumns: `minmax(90px, 1fr) 96px minmax(210px, 1.4fr) 20px${canEdit ? ' 22px' : ''}`,
                      gap: 8, alignItems: 'center',
                      padding: '6px 8px',
                      background: stale ? 'rgba(212,85,85,0.06)' : 'var(--bg-card)',
                      border: `1px solid ${stale ? 'rgba(212,85,85,0.3)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-sm)',
                      opacity: deletingId === c.id ? 0.5 : 1,
                    }}
                  >
                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.characterName}
                      </div>
                      {stale && (
                        <div style={{ fontSize: 9, color: 'var(--s-incomplete-t)', marginTop: 2 }}>
                          ⚠ no longer in script
                        </div>
                      )}
                    </div>
                    <select
                      value={c.camAssignment || ''}
                      disabled={!canEdit || savingId === c.id}
                      onChange={e => patchCharacter(c.id, { camAssignment: e.target.value ? Number(e.target.value) : null })}
                      className="form-input"
                      style={{ fontSize: 11, padding: '3px 6px' }}
                    >
                      <option value="">Cam…</option>
                      <option value={1}>Cam 1</option>
                      <option value={2}>Cam 2</option>
                      <option value={3}>Cam 3</option>
                      <option value={4}>Cam 4</option>
                    </select>
                    {/* Run 2 / Build 3: per-script talent override. Changing this
                        casts THIS script only — the client default is untouched.
                        §3.1: with no talent set the row is UNCAST; the registry's
                        casting note renders muted+italic behind an explicit marker,
                        never as a bare name.
                        DEF-108: the chip is a BUTTON that opens the picker; the cell
                        clips its overflow so no click can land on the ✕.
                        D-111: no override + registry default → muted default chip. */}
                    <div data-testid="talent-cell" style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, overflow: 'hidden' }}>
                      {(() => {
                        const note = registryNote(c.characterName);
                        const def  = c.defaultTalent || null;
                        const picking = pickingId === c.id;
                        const chipBase = {
                          padding: '0 6px', borderRadius: 3, fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em',
                          textTransform: 'uppercase', lineHeight: '17px', flexShrink: 0, fontFamily: 'var(--font)',
                          whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
                        };
                        const uncastStyle  = { ...chipBase, background: 'rgba(140,140,160,0.14)', color: 'var(--tx-muted)', border: '1px dashed var(--border-mid)' };
                        const defaultStyle = { ...chipBase, background: 'transparent', color: 'var(--tx-muted)', border: '1px solid var(--border)', textTransform: 'none', fontSize: 10.5, fontWeight: 600, letterSpacing: 0 };
                        const picker = canEdit && (
                          <select
                            autoFocus={picking}
                            data-testid="talent-picker"
                            value={c.talentId || ''}
                            disabled={savingId === c.id}
                            onChange={e => patchCharacter(c.id, { talentId: e.target.value || null })}
                            onBlur={() => { if (!c.talentId) setPickingId(null); }}
                            onKeyDown={e => { if (e.key === 'Escape') setPickingId(null); }}
                            className="form-input"
                            style={{ fontSize: 11, padding: '3px 6px', minWidth: 0, flex: 1, maxWidth: 190 }}
                            title={def ? `Override for this script only — leave on “Project default” to follow the registry (${def.name})` : 'Cast for this script only — the project default is unchanged'}
                          >
                            <option value="">{def ? `Project default · ${def.name}` : 'Talent…'}</option>
                            {talents.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            {c.talentId && !talents.some(t => t.id === c.talentId) && (
                              <option value={c.talentId}>{c.talent?.name || 'Unknown member'}</option>
                            )}
                          </select>
                        );
                        // Cast for this script (override): the picker shows the name, editable in place.
                        if (c.talentId) {
                          return canEdit ? picker : (
                            <span style={{ fontSize: 11, color: 'var(--tx-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.talent?.name}</span>
                          );
                        }
                        // Picker open (chip clicked): the select replaces the chip until a choice / Escape / blur.
                        if (picking && canEdit) return picker;
                        // No override — project default (muted, D-111) or UNCAST (dashed). Both open the picker.
                        const chip = def ? (
                          <button
                            type="button"
                            data-testid="default-talent-chip"
                            disabled={!canEdit}
                            onClick={e => { e.stopPropagation(); if (canEdit) setPickingId(c.id); }}
                            title={canEdit ? `${def.name} — project default from the character registry (not yet booked). Click to cast someone else for this script only.` : `${def.name} — project default from the character registry`}
                            style={{ ...defaultStyle, cursor: canEdit ? 'pointer' : 'default' }}
                          >{def.name}<span style={{ opacity: 0.6, marginLeft: 5, fontSize: 8.5, letterSpacing: '0.05em' }}>DEFAULT</span></button>
                        ) : (
                          <button
                            type="button"
                            data-testid="uncast-chip"
                            disabled={!canEdit}
                            onClick={e => { e.stopPropagation(); if (canEdit) setPickingId(c.id); }}
                            title={note
                              ? `Uncast — the writers' note (“${note}”) is not a casting.${canEdit ? ' Click to pick talent for this script.' : ''}`
                              : (canEdit ? 'Uncast — click to pick talent for this script (or set a project default in the character registry)' : 'Uncast — a Producer, Power User or Admin casts this character')}
                            style={{ ...uncastStyle, cursor: canEdit ? 'pointer' : 'default' }}
                          >uncast{canEdit ? ' ▾' : ''}</button>
                        );
                        return (
                          <>
                            {chip}
                            {note && (
                              <span style={{ fontSize: 10, fontStyle: 'italic', color: 'var(--tx-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {note}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <span style={{ fontSize: 11, textAlign: 'center', color: c.talReady ? '#4ade80' : 'var(--tx-faint)' }}>
                      {c.talReady ? '✓' : '·'}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => deleteCharacter(c.id, c.characterName)}
                        disabled={deletingId === c.id || savingId === c.id}
                        title="Remove this character"
                        style={{
                          width: 22, height: 22, padding: 0,
                          background: 'transparent', border: 'none',
                          color: 'var(--tx-faint)', cursor: 'pointer',
                          fontSize: 14, lineHeight: 1, fontFamily: 'var(--font)',
                          borderRadius: 4,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--s-incomplete-t)'; e.currentTarget.style.background = 'rgba(212,85,85,0.08)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--tx-faint)'; e.currentTarget.style.background = 'transparent'; }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Status line — no send button: Script List membership is derived
              (D-050), so the script is already there. Tags remain useful: cam
              tags drive the Schedule Plan modal's grouping. */}
          {canEdit && characters.length > 0 && (
            <div style={{
              marginTop: 14, paddingTop: 12,
              borderTop: '1px solid var(--border)',
              fontSize: 11, color: 'var(--tx-muted)',
            }}>
              {scheduled ? (
                hasShootDay ? (
                  <span style={{ color: 'var(--violet)' }}>
                    Scheduled — see the Shoot Schedule (also listed on the Script List's Assigned tab).
                  </span>
                ) : (
                  <span style={{ color: 'rgba(230,200,110,0.95)' }}>
                    Assigned — no shoot day yet. Book talent on the Talent Calendar, then Schedule from the day's Shootable Scripts.
                  </span>
                )
              ) : talStatus === 'not_ready' ? (
                <span>
                  Hidden from the Script List's Ready tab — recover it there with "Show hidden".
                </span>
              ) : (
                <span>
                  <span style={{ color: '#4ade80' }}>In the Script List</span>
                  {untagged.length > 0 && (
                    <span>
                      {' '}· {untagged.length} character{untagged.length === 1 ? '' : 's'} untagged —
                      untagged characters plan as Cam 1; add gender + cam tags to refine cam grouping.
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
